import StyledText from "../../src/typography/typography-component"
import { partition } from "lodash"
import DOMPurify from "isomorphic-dompurify"
import styles from "./ratgeber.module.scss"
import {
  RatgeberArticlePageWithLocalImage,
  RatgeberArticlesResponse,
  RatgeberCategoryPageWithLocalImage,
  RatgeberCategoryResponse,
  RatgeberIndexResponse,
} from "../../src/ratgeber/_types"
import { Divider } from "@mui/material"
import skColors from "../../styles/skColors"
import BehindNavBar from "../../src/components/BehindNavBar/BehindNavBar"
import ArticleCard from "../../src/components/ArticleCard/ArticleCard"
import { Fragment } from "react"
import Footer from "../../src/components/footer"
import * as Sentry from "@sentry/nextjs"
import NavBarNotOnHomepage from "../../src/components/LandingPage/navbars/NavBarNotOnHomepage"
import { fetchAndCacheImage } from "../../src/utils/fetchAndCacheImages"
import axios from "axios"
import { GetStaticProps } from "next"
import NavbarQuickfix from "../../src/components/LandingPage/navbars/NavBarTranslationsQuickFix"
import { useTypedRouter } from "../../src/data/routes"
import FAQ from "../../src/content/faq/faq"
import SkinuvitaSeoHead from "../../src/components/LandingPage/SkinuvitaSeoHead"
import { breadcrumbSchema } from "../../src/seo/jsonLd"
import { buildCanonicalUrl } from "../../src/seo/siteConfig"
import CategoryButtons from "../../src/ratgeber/components/CategoryButtons/CategoryButtons"

// Fetch all pages from paginated API endpoint
const fetchAllPages = async (url: string) => {
  const allItems: any[] = []
  let offset = 0
  const limit = 20
  let hasMore = true

  while (hasMore) {
    try {
      // Try with offset/limit first
      let fetchUrl = url
      if (url.includes("?")) {
        fetchUrl = `${url}&offset=${offset}&limit=${limit}`
      } else {
        fetchUrl = `${url}?offset=${offset}&limit=${limit}`
      }

      const res = await axios.get(fetchUrl)
      const items = res.data.items || []

      if (items.length === 0) {
        hasMore = false
      } else {
        allItems.push(...items)
        offset += items.length
        hasMore = items.length === limit
      }
    } catch (error: any) {
      console.warn(`Failed to fetch at offset ${offset}:`, error?.response?.status || error?.message)
      hasMore = false
    }
  }

  return allItems
}

export const getStaticProps: GetStaticProps<BlogPageProps> = async (context) => {
  // Call an external API endpoint to get ratgeberIndexPage
  let ratgeberIndexPage: RatgeberIndexResponse = {
    meta: {
      total_count: 0,
    },
    items: [],
  }
  let ratgeberCategoryPagesWithImages: RatgeberCategoryPageWithLocalImage[] = []
  let ratgeberArticlePagesWithImages: RatgeberArticlePageWithLocalImage[] = []
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?type=ratgeber.RatgeberIndexPage&fields=*`
    )
    if (res.ok) {
      ratgeberIndexPage = await res.json()
    } else {
      console.warn(`Failed to fetch ratgeber index page: ${res.status}`)
    }
  } catch (e) {
    console.error("Failed to fetch ratgeber index page", e)
    Sentry.captureException(e)
  }

  try {
    const categoryResponse = await axios.get<RatgeberCategoryResponse>(
      `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?type=ratgeber.RatgeberCategoryPage&fields=*`
    )
    const categories = categoryResponse.data.items || []

    ratgeberCategoryPagesWithImages = await Promise.all(
      categories.map(async (child) => {
        let localImagePath: string | null = null
        if (child?.banner_image?.full_url) {
          const filename = `${child.meta.slug}-${child.banner_image.id}.webp`
          try {
            localImagePath = await fetchAndCacheImage(child.banner_image.full_url, filename)
          } catch (imageError) {
            console.warn(`Failed to cache category image for ${child.meta.slug}`, imageError)
          }
        }
        return {
          ...child,
          banner_image: localImagePath,
        }
      })
    )
  } catch (e) {
    console.error("Failed to fetch ratgeber categories", e)
    Sentry.captureException(e)
  }

  try {
    const articlesResponse = await fetchAllPages(
      `${process.env.NEXT_PUBLIC_CONTENT_URL}/api/v2/pages/?order=-first_published_at&type=ratgeber.RatgeberArticlePage&fields=*`
    )

    const articlePages = articlesResponse || []
    ratgeberArticlePagesWithImages = await Promise.all(
      articlePages.map(async (child) => {
        let localImagePath: string | null = null
        if (child.banner_image?.full_url) {
          const filename = `${child.meta.slug}-${child.banner_image.id}.webp`
          try {
            localImagePath = await fetchAndCacheImage(child.banner_image.full_url, filename)
          } catch (imageError) {
            console.warn(`Failed to cache article image for ${child.meta.slug}`, imageError)
          }
        }
        return {
          ...child,
          banner_image: { local_url: localImagePath, title: child.banner_image.title },
        }
      })
    )
  } catch (e) {
    console.error("Failed to fetch ratgeber articles", e)
    Sentry.captureException(e)
  }
  const locale = (context.locale as string) || "de"

  let messages
  try {
    messages = (await import(`../../src/intl/${locale}.json`)).default
  } catch (err) {
    // fallback to default
    messages = (await import(`../../src/intl/de.json`)).default
  }

  // By returning { props: { ratgeberIndexPage } }, the Blog component
  // will receive `ratgeberIndexPage` as a prop at build time
  return {
    // revalidate: 60 * 10, // 10 min
    props: {
      ratgeberIndexPage,
      ratgeberCategoryPagesWithImages,
      ratgeberArticlePagesWithImages,
      messages,
      locale,
    },
  }
}

type BlogPageProps = {
  ratgeberIndexPage?: RatgeberIndexResponse
  ratgeberCategoryPagesWithImages?: RatgeberCategoryPageWithLocalImage[]
  ratgeberArticlePagesWithImages?: RatgeberArticlePageWithLocalImage[]
}
export default function Blog({
  ratgeberIndexPage,
  ratgeberCategoryPagesWithImages,
  ratgeberArticlePagesWithImages,
}: BlogPageProps) {
  // console.log("ratgeberArticlePagesWithImages")
  // console.log(ratgeberArticlePagesWithImages)
  const router = useTypedRouter()
  const ratgeberIndexObject = ratgeberIndexPage?.items?.length > 0 && ratgeberIndexPage.items[0]
  if (!ratgeberIndexObject) {
    return (
      <div className={styles.root} key={"ratgeber-index-page"}>
        <NavbarQuickfix />
        <BehindNavBar />
        <div className={styles.ratgeberPage} key={"ratgeber-index-page-div"}>
          <div className={styles.ratgeberIntro}>
            <StyledText className={styles.ratgeberPageTitle} variant={"h1"}>{`Ratgeber\n`}</StyledText>
            <div className={styles.sanitizedHtml}>
              <StyledText
                variant={"pRegular"}
              >{`Momentan können die Artikel nicht geladen werden. Versuchen Sie es später erneut.`}</StyledText>
            </div>
          </div>
          <div className={styles.categoryButtonList}>
            <StyledText variant={"pRegular"}>{`Keine Kategorien gefunden`}</StyledText>
          </div>
        </div>
      </div>
    )
  }
  const sanitizedHtml = DOMPurify.sanitize(ratgeberIndexObject.body)

  // console.log("ratgeberArticlePagesWithImages")
  // console.log(ratgeberArticlePagesWithImages)
  console.log(ratgeberArticlePagesWithImages.length)
  const [articles, featuredArticles] = partition(ratgeberArticlePagesWithImages, (item) => !item.isFeatured)

  const articleComponents = articles
    .slice(0, 6)
    .sort((articleA, articleB) => {
      const dateA = new Date(articleA.meta.first_published_at)
      const dateB = new Date(articleB.meta.first_published_at)

      return dateA > dateB ? -1 : 1
    })
    .map((article) => {
      return <ArticleCard key={article.id} article={article} />
    })
  const featuredArticleComponents = featuredArticles.map((article) => {
    return <ArticleCard isFeatured={true} key={article.id} article={article} />
  })

  return (
    <>
      <SkinuvitaSeoHead
        title={`${ratgeberIndexObject.title} | Skinuvita`}
        description={
          "Redaktionell geprüfte Artikel zu UVB-Lichttherapie, Schuppenflechte, Neurodermitis " +
          "und dem Alltag mit chronisch-entzündlichen Hauterkrankungen."
        }
        canonicalPath={"/ratgeber"}
        jsonLd={[
          breadcrumbSchema([
            { name: "Startseite", url: buildCanonicalUrl("/") },
            { name: "Ratgeber", url: buildCanonicalUrl("/ratgeber") },
          ]),
        ]}
      />
      {/*<NavbarQuickfix />*/}
      <NavBarNotOnHomepage />
      <div className={styles.root} key={"ratgeber-index-page"}>
        <div>
          <BehindNavBar />
          <div className={styles.ratgeberPage} key={"ratgeber-index-page-div"}>
            <div className={styles.ratgeberIntro}>
              <StyledText
                className={styles.ratgeberPageTitle}
                variant={"h1"}
              >{`${ratgeberIndexObject.title}\n`}</StyledText>
              <div className={styles.sanitizedHtml} dangerouslySetInnerHTML={{ __html: sanitizedHtml }}></div>
            </div>
            <CategoryButtons categories={ratgeberIndexObject.categories} />
            <div className={styles.allArticles}>
              {featuredArticles.length > 0 && (
                <div className={styles.featuredArticlesSection} key={"featured-articles"}>
                  <StyledText variant={"h2"} color={skColors.darkgreen[600]}>
                    {`Featured`}
                  </StyledText>
                  <div key={"featuredArticleComponents-div"} className={styles.ratgeberFeaturedArticlesContainer}>
                    {featuredArticleComponents}
                  </div>
                </div>
              )}
              <div className={styles.ratgeberArticles} key={"all-articles"}>
                <StyledText variant={"h2"} color={skColors.darkgreen[600]}>
                  {`Neueste`}
                </StyledText>
                <div key={"articleComponents-div"} className={styles.ratgeberArticlesContainer}>
                  {articleComponents}
                </div>
              </div>
            </div>
          </div>
        </div>
        {ratgeberIndexObject.faqs.length > 0 && <FAQ faqs={ratgeberIndexObject.faqs} />}
        <Footer />
      </div>
    </>
  )
}
